/**
 * The governed lens registry: each lens is a versioned JSON file under
 * `./lenses/`, validated at load. An invalid lens fails loudly here, never
 * silently at narration time. Org/workforce lands with its shapes (S3).
 */
import maIntegration from './lenses/ma-integration.lens.v1.json';
import finance from './lenses/finance.lens.v1.json';
import type { AskStoryline } from '../contracts/declared-storyline';
import { verbalizeAction, verbalizeFact } from './microgrammars';
import { narrativeLensProblems, type AskNarrativeLens } from './narrative-lens';
import type { AskNarrativePlan } from './narrative-plan';

function loadLens(raw: unknown): AskNarrativeLens {
  const lens = raw as AskNarrativeLens;
  const problems = narrativeLensProblems(lens);
  if (problems.length > 0) throw new Error(`narrative lens ${lens.lensId}: ${problems.join('; ')}`);
  return lens;
}

export const ASK_NARRATIVE_LENSES: readonly AskNarrativeLens[] = Object.freeze([
  loadLens(maIntegration),
  loadLens(finance),
]);

/**
 * The story a plan verbalizes to: the governing fact's grammar as the lede,
 * supporting facts as drivers, the lens's reviewed qualification sentences and
 * follow-up labels. No lede grammar → no story (the exhibit stands alone).
 */
export function storyFromPlan(
  plan: AskNarrativePlan,
  lens: AskNarrativeLens | null
): AskStoryline | null {
  const lede = verbalizeFact(plan.governing);
  if (lede === null) return null;
  const drivers = plan.supporting
    .map(verbalizeFact)
    .filter((sentence): sentence is NonNullable<typeof sentence> => sentence !== null);
  const qualifications = plan.qualificationIds.flatMap(
    (id) => lens?.qualifications.filter((item) => item.id === id).map((item) => item.text) ?? []
  );
  const recommendation = verbalizeAction(plan.action, plan.governing);
  const followUps = plan.followUpIds.flatMap(
    (id) =>
      lens?.followUps
        .filter((item) => item.id === id)
        .map(({ id: key, label }) => ({ id: key, label })) ?? []
  );
  return {
    lede,
    ...(drivers.length > 0 ? { drivers } : {}),
    ...(qualifications.length > 0 ? { qualifications } : {}),
    ...(followUps.length > 0 ? { followUps } : {}),
    ...(recommendation ? { recommendation: { text: recommendation } } : {}),
  };
}
